import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Ban, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errorText, ROLE_LEVELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, Btn, Input, Textarea, Avatar, RoleBadge, Spinner } from "@/forum/ui";
import AdminThreads from "./AdminThreads";

const ROLES = [["player", "Játékos"], ["moderator", "Moderátor"], ["admin", "Admin"], ["owner", "Tulajdonos"]];
const USER_ROLES = [["member", "Tag"], ...ROLES];
const EMPTY = { name: "", description: "", group: "Közösség", icon: "MessageSquare", min_read_role: "player", min_write_role: "player", order: 0 };

const Select = ({ value, onChange, options, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="rounded-xl border border-white/10 bg-[#161619] px-3 py-2 text-sm text-white outline-none focus:border-[rgba(94,177,255,0.6)]" data-testid={testId}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

const CategoryForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target?.value ?? e }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ ...f, order: Number(f.order) }); }} className="space-y-3 rounded-2xl border border-[rgba(94,177,255,0.3)] bg-white/[0.02] p-4" data-testid="category-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <Input value={f.name} onChange={set("name")} placeholder="Kategória neve" required minLength={2} data-testid="cat-name" />
        <Input value={f.group} onChange={set("group")} placeholder="Csoport (pl. Közösség)" data-testid="cat-group" />
      </div>
      <Textarea value={f.description} onChange={set("description")} rows={2} placeholder="Rövid leírás" data-testid="cat-description" />
      <div className="grid gap-3 sm:grid-cols-4">
        <Input value={f.icon} onChange={set("icon")} placeholder="Lucide ikon neve" data-testid="cat-icon" />
        <Input type="number" value={f.order} onChange={set("order")} placeholder="Sorrend" data-testid="cat-order" />
        <label className="flex flex-col gap-1 font-mono text-[10px] uppercase tracking-wider text-white/40">Olvasás min.<Select value={f.min_read_role} onChange={set("min_read_role")} options={ROLES} testId="cat-read-role" /></label>
        <label className="flex flex-col gap-1 font-mono text-[10px] uppercase tracking-wider text-white/40">Írás min.<Select value={f.min_write_role} onChange={set("min_write_role")} options={ROLES} testId="cat-write-role" /></label>
      </div>
      <div className="flex justify-end gap-2">
        <Btn variant="ghost" type="button" onClick={onCancel} data-testid="cat-cancel">Mégse</Btn>
        <Btn type="submit" data-testid="cat-save">Mentés</Btn>
      </div>
    </form>
  );
};

export default function AdminPage() {
  const { user, level } = useAuth();
  const [cats, setCats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [deleting, setDeleting] = useState(null);

  const loadCats = () => api.get("/forum/categories").then((r) => setCats(r.data));
  const loadUsers = (query = "") => api.get("/forum/admin/users", { params: { q: query } }).then((r) => setUsers(r.data));
  useEffect(() => { Promise.all([loadCats(), loadUsers()]).catch((e) => toast.error(errorText(e))); }, []);

  const run = async (fn, ok) => { try { await fn(); toast.success(ok); } catch (e) { toast.error(errorText(e)); } };
  const saveCat = (data) => run(async () => {
    if (editing === "new") await api.post("/forum/categories", data); else await api.put(`/forum/categories/${editing}`, data);
    setEditing(null); await loadCats();
  }, "Kategória mentve");
  const delCat = (c) => window.confirm(`Törlöd a(z) „${c.name}” kategóriát minden témájával?`) && run(async () => { await api.delete(`/forum/categories/${c.id}`); await loadCats(); }, "Kategória törölve");
  const setRole = (u, role) => run(async () => { await api.put(`/forum/admin/users/${u.id}/role`, { role }); await loadUsers(q); }, `${u.username} új rangja: ${role}`);
  const toggleBan = (u) => run(async () => { await api.put(`/forum/admin/users/${u.id}/ban`, { banned: !u.banned }); await loadUsers(q); }, u.banned ? "Tiltás feloldva" : "Felhasználó kitiltva");
  const deleteUser = async (u) => {
    if (!window.confirm(`Végleg törlöd ${u.username} felhasználót, minden saját témáját és hozzászólását? A témáiban mások válaszai is törlődnek. Ez nem vonható vissza. A játékfiókja megmarad.`)) return;
    setDeleting(u.id);
    await run(async () => {
      await api.delete(`/forum/admin/users/${u.id}`);
      setUsers((current) => current.filter((item) => item.id !== u.id));
      await Promise.all([loadUsers(q), loadCats()]);
    }, "Felhasználó és fórumtartalmai törölve");
    setDeleting(null);
  };

  const assignable = USER_ROLES.filter(([r]) => level === 3 || ROLE_LEVELS[r] < level);

  return (
    <div className="space-y-10" data-testid="admin-page">
      <div>
        <p className="eyebrow mb-3">Adminisztráció</p>
        <h1 className="font-display text-3xl font-bold">Fórum kezelése</h1>
      </div>

      <section className="space-y-4" data-testid="admin-categories">
        <div className="flex items-center justify-between">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">Kategóriák</h2>
          <Btn variant="ghost" onClick={() => setEditing("new")} data-testid="add-category"><Plus size={14} />Új kategória</Btn>
        </div>
        {editing === "new" && <CategoryForm initial={EMPTY} onSave={saveCat} onCancel={() => setEditing(null)} />}
        {!cats ? <Spinner /> : (
          <Panel className="divide-y divide-white/[0.05] overflow-hidden">
            {cats.map((c) => editing === c.id ? (
              <div key={c.id} className="p-3"><CategoryForm initial={c} onSave={saveCat} onCancel={() => setEditing(null)} /></div>
            ) : (
              <div key={c.id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid={`admin-cat-${c.slug}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-display font-semibold">{c.name} <span className="font-mono text-[10px] text-white/35">· {c.group}</span></p>
                  <p className="font-mono text-[10px] text-white/40">olvasás: {c.min_read_role} · írás: {c.min_write_role} · {c.thread_count} téma</p>
                </div>
                <button type="button" onClick={() => setEditing(c.id)} className="grid h-8 w-8 place-items-center rounded-full border border-white/10 text-white/60 hover:text-white" data-testid={`edit-cat-${c.slug}`}><Pencil size={12} /></button>
                <button type="button" onClick={() => delCat(c)} className="grid h-8 w-8 place-items-center rounded-full border border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid={`delete-cat-${c.slug}`}><Trash2 size={12} /></button>
              </div>
            ))}
          </Panel>
        )}
      </section>

      <AdminThreads categories={cats} onSaved={loadCats} />

      <section className="space-y-4" data-testid="admin-users">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">Felhasználók és rangok</h2>
          <Input value={q} onChange={(e) => { setQ(e.target.value); loadUsers(e.target.value).catch((ex) => toast.error(errorText(ex))); }} placeholder="Keresés felhasználónévre…" className="sm:w-64" data-testid="user-search" />
        </div>
        <Panel className="divide-y divide-white/[0.05] overflow-hidden">
          {users.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid={`admin-user-${u.username}`}>
              <Avatar user={u} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{u.username} {u.banned && <span className="font-mono text-[10px] text-red-300">· kitiltva</span>}</p>
                <p className="font-mono text-[10px] text-white/40">{u.email} · {u.post_count} hozzászólás</p>
              </div>
              <RoleBadge role={u.role} label={u.role_label} />
              {u.id !== user.id && (ROLE_LEVELS[u.role] < level || level === 3) ? (
                <>
                  <Select value={u.role} onChange={(r) => setRole(u, r)} options={assignable.some(([r]) => r === u.role) ? assignable : [...assignable, [u.role, u.role_label]]} testId={`role-select-${u.username}`} />
                  <button type="button" onClick={() => toggleBan(u)} title={u.banned ? "Tiltás feloldása" : "Kitiltás"} className={`grid h-8 w-8 place-items-center rounded-full border ${u.banned ? "border-emerald-400/40 text-emerald-300" : "border-red-500/30 text-red-300"}`} data-testid={`ban-${u.username}`}>
                    {u.banned ? <ShieldCheck size={12} /> : <Ban size={12} />}
                  </button>
                </>
              ) : <span className="font-mono text-[10px] text-white/30">—</span>}
              {user.role === "owner" && u.id !== user.id && u.role !== "owner" && (
                <button type="button" disabled={deleting !== null} onClick={() => deleteUser(u)} title="Felhasználó végleges törlése" aria-label={`${u.username} törlése`} className="grid h-8 w-8 place-items-center rounded-full border border-red-500/30 text-red-300 hover:bg-red-500/10 disabled:opacity-50" data-testid={`delete-user-${u.username}`}><Trash2 size={12} /></button>
              )}
            </div>
          ))}
        </Panel>
      </section>
    </div>
  );
}
